import type { QpxHeroIconContext } from '@quipex/shared/ui';

export type BuildingDatapointCategory =
    | 'size-and-form'
    | 'use-occupancy-classification'
    | 'sustainability-and-performance'
    | 'records-and-identity';

export type BuildingOverviewDatapointKey =
    | 'about'
    | 'floor_area'
    | 'lots'
    | 'occupancy_issued'
    | 'effective_height';

export interface BuildingDatapointDefinition {
    readonly label: string;
    readonly definition: string;
    readonly citation?: { readonly label: string; readonly url: string };
}

export const GEOSCAPE_BUILDINGS_GUIDE_URL =
    'https://docs.geoscape.com.au/projects/buildings_guide/en/stable/';

export const BUILDING_OVERVIEW_DATAPOINTS: Record<
    BuildingOverviewDatapointKey,
    BuildingDatapointDefinition & {
        readonly icon: string;
        readonly category: BuildingDatapointCategory;
    }
> = {
    about: {
        label: 'About',
        definition: 'General description of the building.',
        icon: 'lucideSubtitles',
        category: 'records-and-identity',
    },
    floor_area: {
        label: 'Total floor area',
        definition: 'Combined floor space across all building levels.',
        citation: {
            label: 'Geoscape',
            url: `${GEOSCAPE_BUILDINGS_GUIDE_URL}#building-area-information`,
        },
        icon: 'qpxBuildingFloorArea',
        category: 'size-and-form',
    },
    lots: {
        label: 'Lots',
        definition: 'Number of separate premises associated with the building.',
        citation: {
            label: 'Geoscape',
            url: `${GEOSCAPE_BUILDINGS_GUIDE_URL}#address-information`,
        },
        icon: 'lucideLandPlot',
        category: 'size-and-form',
    },
    occupancy_issued: {
        label: 'Occupancy issued',
        definition: 'Date the occupancy certificate was issued.',
        icon: 'lucideCalendarCheck2',
        category: 'records-and-identity',
    },
    effective_height: {
        label: 'Effective height',
        definition:
            'Vertical distance from the floor of the lowest counted storey to the floor of the highest storey.',
        citation: {
            label: 'NCC glossary',
            url: 'https://ncc.abcb.gov.au/editions/ncc-2022/adopted/housing-provisions/1-definitions/glossary',
        },
        icon: 'qpxBuildingEffectiveHeight',
        category: 'size-and-form',
    },
};

const DATAPOINT_ICON_CONTEXTS = [
    'info',
    'primary',
    'success',
    'secondary',
    'caution',
] as const satisfies readonly QpxHeroIconContext[];

export function contextForDatapointCategory(
    category: BuildingDatapointCategory
): QpxHeroIconContext {
    let hash = 2166136261;
    for (const character of category) {
        hash ^= character.codePointAt(0) ?? 0;
        hash = Math.imul(hash, 16777619);
    }

    return DATAPOINT_ICON_CONTEXTS[
        (hash >>> 0) % DATAPOINT_ICON_CONTEXTS.length
    ];
}
